import { NavFn } from '../../pages/SchedulerPage'
import { Page, FilterChip, Segmented, Button } from '@indipia/ui-core'
import { OnboardingTour, type OnboardingTourHandle, type TourStep } from '../OnboardingTour'
import { GanttInfoBar } from './GanttInfoBar'
import { SHIP_GANTT_CHIP_LABELS } from '../../constants/ship.logic'
import { GANTT_PERIOD_OPTIONS } from '../../constants/gantt.const'
import { SCHEDULE_VIEW_OPTIONS } from '../../views/scheduleUtils'
import { EmptyState } from '@indipia/ui-core'
// 현장 미선택 화면

export type GanttEmptySiteProps = {
  handlePDF: () => void
  setOpenDd: React.Dispatch<React.SetStateAction<"dd1" | "dd2" | "dd3" | null>>
  openDd: "dd1" | "dd2" | "dd3" | null
  showRel: boolean
  setShowRel: React.Dispatch<React.SetStateAction<boolean>>
  showCont: boolean
  toggleContChip: (_u?: unknown) => void
  showVendor: boolean
  toggleVendorChip: (_u?: unknown) => void
  showMarkers: boolean
  setShowMarkers: React.Dispatch<React.SetStateAction<boolean>>
  showFloat: boolean
  setShowFloat: React.Dispatch<React.SetStateAction<boolean>>
  hideBars: boolean
  setHideBars: React.Dispatch<React.SetStateAction<boolean>>
  ganttPeriod: "전체" | "1주" | "2주" | "월"
  setGanttPeriod: React.Dispatch<React.SetStateAction<"전체" | "1주" | "2주" | "월">>
  navigate: NavFn
  /** 회사에 스케줄이 하나도 없음(검색·필터 조건 없이 0건) — C-0 안내와 [공사스케줄생성] 표시(F-12) */
  noSchedules?: boolean
  /** 화면 가이드 — 미선택 상단바에도 [?]를 두고 화면에 있는 대상만 보여준다(F-07) */
  tourRef: React.RefObject<OnboardingTourHandle>
  tourSteps: TourStep[]
}

export function GanttEmptySite({
  handlePDF, setOpenDd, openDd, showRel, setShowRel, showCont,
  toggleContChip, showVendor, toggleVendorChip, showMarkers, setShowMarkers, showFloat, setShowFloat,
  hideBars, setHideBars, ganttPeriod, setGanttPeriod, navigate, noSchedules, tourRef, tourSteps,
}: GanttEmptySiteProps) {
  return (
    <Page layout="fixed">

      {/* Info bar — 현장 미선택 모드(현장 의존 기능 비활성) */}
      <GanttInfoBar handlePDF={handlePDF} setOpenDd={setOpenDd} openDd={openDd} tourRef={tourRef} />

      {/* Options bar */}
      <div style={{ background: 'var(--shell-bg)', padding: '6px 18px', borderBottom: 'var(--border-width-hairline) solid var(--shell-border)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {/* 칩 그룹 — 바 숨기기까지 한 덩어리. 간격은 --spacing-02(4px)로 Options bar 전체 gap 8과 분리 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-02)', flexShrink: 0 }}>
          {([
            { label: '선후관계', val: showRel,     set: setShowRel,     icon: null },
            { label: SHIP_GANTT_CHIP_LABELS.foreman, val: showCont,    set: toggleContChip,   icon: null },
            { label: SHIP_GANTT_CHIP_LABELS.vendor, val: showVendor,  set: toggleVendorChip, icon: null },
            { label: '발주·입고', val: showMarkers, set: setShowMarkers, icon: <svg key="ms" width="7" height="7" viewBox="0 0 12 12"><polygon points="6,0 12,6 6,12 0,6" fill="currentColor"/></svg> },
            { label: '대기일',   val: showFloat,   set: setShowFloat,   icon: null },
          ] as const).map(({ label, val, set, icon }) => (
            <FilterChip key={label} size="lg" active={val} onClick={() => set((v: boolean) => !v)}>
              {icon}{label}
            </FilterChip>
          ))}
          <FilterChip size="lg" active={hideBars} onClick={() => setHideBars(v => !v)}>
            {hideBars ? (
              <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>바 보기</>
            ) : (
              <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>바 숨기기</>
            )}
          </FilterChip>
        </div>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--shell-on-surface-2)', letterSpacing: '.3px', whiteSpace: 'nowrap' }}>범위</span>
        <Segmented className="!shrink-0" options={GANTT_PERIOD_OPTIONS} value={ganttPeriod} onChange={v => setGanttPeriod(v as typeof ganttPeriod)} />
        <div style={{ width: 1, height: 16, background: 'var(--shell-border)', margin: '0 2px', flexShrink: 0 }} />
        <span style={{ fontSize: 'var(--type-caption-size)', fontWeight: 600, color: 'var(--shell-on-surface-2)', letterSpacing: '.3px', whiteSpace: 'nowrap' }}>보기</span>
        <Segmented
          className="!shrink-0"
          options={SCHEDULE_VIEW_OPTIONS}
          value="gantt"
          onChange={v => { if (v === 'cal-b2') navigate('cal-b2'); else if (v === 'work-type') navigate('work-type') }}
        />
      </div>

      {/* Empty body */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {noSchedules ? (
          // 빈 상태 액션은 secondary — §15.1 "주 액션=primary"의 명시적 예외(D-07 §14.2 · D-09 §15.37)
          <EmptyState
            title="아직 생성된 공사 스케줄이 없어요"
            description="엑셀 견적서만 올리면 AI가 자동으로 만들어드려요 · 직접 만들기도 쉬워요"
            action={
              <Button variant="secondary" className="gap-1.5" onClick={() => navigate('create-site')}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                공사스케줄생성
              </Button>
            }
          />
        ) : (
          <EmptyState title="현장을 선택해주세요" description="오른쪽 패널에서 현장 카드를 클릭하세요" />
        )}
      </div>

      {/* 화면 가이드 — [?] 클릭으로만 재생 */}
      <OnboardingTour ref={tourRef} storageKey="indipia_gantt_tour_seen_v1" steps={tourSteps} autoStart={false} />
    </Page>
  )
}
